import { geminiRateLimiter } from '../lib/rateLimiter';
import { supabase } from '../lib/supabase';
import { SCHEMA_DOC, parseSpecFromResponse, AiDiagramSpec } from '../lib/whiteboardAiSchema';

/**
 * Whiteboard AI assistant.
 *
 * Calls the same Gemini Cloud Function proxy the rest of the app uses, so the
 * API key never reaches the browser. The model returns a compact diagram spec
 * (see whiteboardAiSchema) which the caller expands into Excalidraw elements.
 */

const API_BASE = process.env.REACT_APP_FUNCTIONS_BASE_URL ?? '';

/** Trimmed view of a scene element — what the model needs to reason about
 *  existing content without blowing the prompt budget. */
export interface SceneSummaryItem {
  id: string;
  type: string;
  text?: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ChatTurn {
  role: 'user' | 'assistant';
  content: string;
}

export interface AiDiagramResult {
  spec: AiDiagramSpec | null;
  /** Prose the model returned alongside the spec, shown in the chat. */
  message: string;
}

/**
 * Condenses the live scene for the prompt.
 *
 * Only labelled/structural elements are included and the list is capped —
 * a large board can hold thousands of elements, which would exceed the
 * 32,000-character limit the backend enforces.
 */
export function summariseScene(elements: any[], limit = 60): SceneSummaryItem[] {
  const meaningful = (elements ?? []).filter(
    el => el && !el.isDeleted && el.type !== 'freedraw'
  );

  // Prefer elements that carry a label — those are what a user refers to.
  const withText = meaningful.filter(el => el.text || el.containerId);
  const rest = meaningful.filter(el => !el.text && !el.containerId);
  const chosen = [...withText, ...rest].slice(0, limit);

  return chosen.map(el => ({
    id: el.id,
    type: el.type,
    text: el.text || undefined,
    x: Math.round(el.x ?? 0),
    y: Math.round(el.y ?? 0),
    width: Math.round(el.width ?? 0),
    height: Math.round(el.height ?? 0),
  }));
}

function buildPrompt(
  instruction: string,
  scene: SceneSummaryItem[],
  history: ChatTurn[],
): string {
  const sceneBlock = scene.length
    ? `CURRENT BOARD CONTENTS (${scene.length} element${scene.length === 1 ? '' : 's'} shown):\n` +
      JSON.stringify(scene, null, 0)
    : 'The board is currently empty.';

  // Only the last few turns — enough for "make it bigger" to resolve, without
  // spending the whole budget on history.
  const historyBlock = history.length
    ? 'RECENT CONVERSATION:\n' +
      history.slice(-6).map(t => `${t.role === 'user' ? 'User' : 'You'}: ${t.content}`).join('\n')
    : '';

  return `
You are a diagramming assistant embedded in an Excalidraw whiteboard. You turn
requests into diagrams: flowcharts, architecture diagrams, mind maps, org charts,
sequence and process flows.

${sceneBlock}

${historyBlock}

USER REQUEST:
${instruction}

Respond with a short sentence describing what you drew, then a JSON code block
holding the diagram spec.

SPEC FORMAT:
${SCHEMA_DOC}

LAYOUT RULES — these determine whether the result is readable:
1. Coordinates are absolute pixels. The canvas origin is top-left; x grows
   right, y grows down.
2. Space nodes generously: at least 220px horizontally between node centres and
   at least 130px vertically. Cramped diagrams are unreadable.
3. Default box size is 180x70. Widen a box rather than letting long text overflow.
4. Lay flows left-to-right, or top-to-bottom for hierarchies. Be consistent.
5. Never overlap nodes. Check your coordinates before emitting them.
6. Use "diamond" for decisions, "ellipse" for start/end, "box" for steps.
7. Colour communicates meaning — green for success/start, red for
   errors/failure, yellow for warnings/decisions, blue for normal steps,
   purple for external systems, gray for notes. Don't colour at random.
8. Add an "icon" node next to a box when it clarifies the concept (a database
   icon by a datastore, a user icon by an actor). Icons are 48x48 by default.
   Use ONLY names from the icon list above.
9. Use "frames" to group related nodes into a labelled region.
10. Label edges when the relationship isn't obvious ("on success", "retry").

MODIFYING THE EXISTING BOARD:
- To add to the board, emit only the NEW nodes/edges. Position them so they do
  not overlap the existing elements listed above.
- To change an existing element, reuse its exact "id" from the board contents
  and provide the updated fields.
- Never delete anything unless the user explicitly asks. Omit the "deletions"
  field entirely otherwise.

CRITICAL:
- Emit exactly one JSON code block.
- Every edge "from"/"to" must reference a node id defined in this same spec, or
  an existing element id from the board contents.
- Keep node text under 40 characters.
`.trim();
}

export const whiteboardAiService = {
  /**
   * Sends an instruction plus board context, returns the parsed diagram spec.
   * Throws on network/HTTP failure so the caller can surface it in the chat.
   */
  async generateDiagram(
    instruction: string,
    scene: SceneSummaryItem[],
    history: ChatTurn[] = [],
  ): Promise<AiDiagramResult> {
    geminiRateLimiter.acquire();

    const prompt = buildPrompt(instruction, scene, history);

    // Every /api route is behind requireAuth (functions/src/index.ts), which
    // expects the Supabase access token as a Bearer header. Without it the
    // call returns 401 before ever reaching Gemini.
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      throw new Error('Your session has expired. Please sign in again.');
    }

    const res = await fetch(`${API_BASE}/api/gemini/generate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ prompt }),
    });

    if (!res.ok) {
      // Distinguish the failures a user can act on from the ones they can't.
      if (res.status === 401) {
        throw new Error('Your session has expired. Please sign in again.');
      }
      if (res.status === 403) {
        throw new Error('Your account is not linked to an active organization.');
      }
      if (res.status === 429) {
        throw new Error('Too many requests — please wait a moment and try again.');
      }

      // Read the body as text first: an error response is not always JSON
      // (gateway timeouts and proxy errors return HTML or plain text), and
      // res.json() would throw and discard the only useful diagnostic.
      const raw = await res.text().catch(() => '');
      let detail = raw;
      try {
        const parsed = JSON.parse(raw) as { error?: string };
        if (parsed?.error) detail = parsed.error;
      } catch { /* not JSON — keep the raw text */ }

      const trimmed = detail.trim().slice(0, 300);
      throw new Error(
        `The assistant failed (HTTP ${res.status})` +
        (trimmed ? `: ${trimmed}` : ' with an empty response body.')
      );
    }

    // A 200 does not guarantee a JSON body of the expected shape — a proxy can
    // return HTML with a 200, and the route returns no text if Gemini blocked
    // the response. Parse defensively so the user sees the cause.
    const rawBody = await res.text();
    let text: string;
    try {
      const parsed = JSON.parse(rawBody) as { text?: string };
      if (typeof parsed?.text !== 'string') {
        throw new Error(
          `The assistant returned an unexpected response: ${rawBody.trim().slice(0, 300)}`
        );
      }
      text = parsed.text;
    } catch (err) {
      if (err instanceof Error && err.message.startsWith('The assistant')) throw err;
      throw new Error(
        `The assistant returned a non-JSON response: ${rawBody.trim().slice(0, 300)}`
      );
    }

    if (!text.trim()) {
      throw new Error(
        'The assistant returned an empty response. This usually means the ' +
        'request was blocked by a content filter — try rephrasing.'
      );
    }

    const spec = parseSpecFromResponse(text);

    // Prose minus the JSON block, for the chat transcript.
    const message = text
      .replace(/```json[\s\S]*?```/gi, '')
      .replace(/```[\s\S]*?```/g, '')
      .replace(/\{[\s\S]*\}/, '')
      .trim();

    return {
      spec,
      message: message || (spec ? 'Done — added that to your board.' : text.trim()),
    };
  },
};
